import { useId, useMemo, useState } from "react";

import { cn } from "../lib/cn";

export interface WeightPoint {
  /** ISO date, `YYYY-MM-DD`. */
  on: string;
  grams: number;
}

export interface WeightChartProps {
  points: readonly WeightPoint[];
  /** Names the series, so no legend box is needed for a single line. */
  title: string;
  /**
   * Index of a point to flag. Drawn with the alert colour *and* an icon and a
   * direct label — a status colour never carries meaning on its own.
   */
  alertIndex?: number | undefined;
  alertLabel?: string | undefined;
  className?: string;
}

const VIEWBOX_WIDTH = 720;
const VIEWBOX_HEIGHT = 260;
const PADDING = { top: 16, right: 56, bottom: 32, left: 44 };

/**
 * A bird's weight over time.
 *
 * One line, one axis. Weight is the earliest reliable signal a small passerine gives,
 * so this is a diagnostic instrument rather than decoration — which is why the marks are
 * thin, the grid is recessive, and only the latest point and any flagged loss carry a
 * label. A number on every point would bury the shape, and the shape is the finding.
 *
 * Drawn as inline SVG rather than with a charting library: the whole thing is a
 * polyline and eleven circles, and a library would cost more bytes than the app's own
 * code for a screen a breeder opens on aviary wifi.
 *
 * Colours come from `--color-chart-*`, which are re-stepped brand hues verified against
 * each mode's surface with the palette validator. The brand primary itself fails as a
 * data mark — too dark, and low enough in chroma to read as grey.
 */
export function WeightChart({
  points,
  title,
  alertIndex,
  alertLabel,
  className,
}: WeightChartProps) {
  const titleId = useId();
  const [hovered, setHovered] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);

  const geometry = useMemo(() => buildGeometry(points), [points]);

  if (points.length === 0) {
    return (
      <p className={cn("text-body text-on-surface-variant", className)}>
        No weighings recorded yet.
      </p>
    );
  }

  const active = hovered ?? points.length - 1;
  const activePoint = points[active];

  return (
    <figure className={cn("flex flex-col gap-sm", className)}>
      <figcaption className="flex items-baseline justify-between gap-md">
        <span id={titleId} className="text-label-caps uppercase text-on-surface-variant">
          {title}
        </span>
        <button
          type="button"
          onClick={() => setShowTable((current) => !current)}
          className="text-label-caps uppercase text-primary underline"
        >
          {showTable ? "Show chart" : "Show table"}
        </button>
      </figcaption>

      {showTable ? (
        // The chart's accessible equivalent, not a fallback: some readers want the
        // numbers, and a line is not a substitute for them.
        <table className="w-full text-data text-on-surface" data-numeric>
          <caption className="sr-only">{title}</caption>
          <thead>
            <tr className="text-label-caps uppercase text-on-surface-variant">
              <th scope="col" className="py-xs text-left">Date</th>
              <th scope="col" className="py-xs text-right">Weight</th>
            </tr>
          </thead>
          <tbody>
            {points.map((point) => (
              <tr key={point.on} className="border-t border-outline-variant">
                <td className="py-xs">{point.on}</td>
                <td className="py-xs text-right">{point.grams} g</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="relative">
          <svg
            viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
            role="img"
            aria-labelledby={titleId}
            className="w-full"
            onMouseLeave={() => setHovered(null)}
          >
            {/* Recessive: horizontal only, so the eye reads the line, not the paper. */}
            {geometry.gridLines.map((line) => (
              <g key={line.value}>
                <line
                  x1={PADDING.left}
                  x2={VIEWBOX_WIDTH - PADDING.right}
                  y1={line.y}
                  y2={line.y}
                  stroke="var(--color-chart-grid)"
                  strokeWidth={1}
                  opacity={0.5}
                />
                <text
                  x={PADDING.left - 8}
                  y={line.y + 4}
                  textAnchor="end"
                  className="fill-on-surface-variant"
                  style={{ fontSize: 11 }}
                >
                  {line.value}
                </text>
              </g>
            ))}

            {/* Crosshair on the point under the pointer. */}
            {hovered !== null && geometry.coords[hovered] ? (
              <line
                x1={geometry.coords[hovered]!.x}
                x2={geometry.coords[hovered]!.x}
                y1={PADDING.top}
                y2={VIEWBOX_HEIGHT - PADDING.bottom}
                stroke="var(--color-chart-grid)"
                strokeWidth={1}
              />
            ) : null}

            <polyline
              points={geometry.coords.map((c) => `${c.x},${c.y}`).join(" ")}
              fill="none"
              stroke="var(--color-chart-series-1)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />

            {geometry.coords.map((coord, index) => {
              const isAlert = index === alertIndex;
              const isActive = index === active;

              return (
                <g key={points[index]!.on}>
                  {/* Hit target far bigger than the mark: a 4px dot is not tappable. */}
                  <circle
                    cx={coord.x}
                    cy={coord.y}
                    r={16}
                    fill="transparent"
                    onMouseEnter={() => setHovered(index)}
                    onFocus={() => setHovered(index)}
                    tabIndex={0}
                    role="button"
                    aria-label={`${points[index]!.on}: ${points[index]!.grams} grams`}
                  />
                  <circle
                    cx={coord.x}
                    cy={coord.y}
                    r={isActive || isAlert ? 5 : 4}
                    fill={isAlert ? "var(--color-chart-alert)" : "var(--color-chart-series-1)"}
                    // A 2px surface ring keeps overlapping marks legible.
                    stroke="var(--color-surface-container-lowest)"
                    strokeWidth={2}
                  />
                </g>
              );
            })}

            {/* Selective direct labels: the latest reading and any flagged loss. */}
            <text
              x={geometry.coords[geometry.coords.length - 1]!.x + 8}
              y={geometry.coords[geometry.coords.length - 1]!.y + 4}
              className="fill-on-surface"
              style={{ fontSize: 12, fontWeight: 500 }}
            >
              {points[points.length - 1]!.grams} g
            </text>

            {alertIndex !== undefined && geometry.coords[alertIndex] ? (
              <text
                x={geometry.coords[alertIndex]!.x}
                y={geometry.coords[alertIndex]!.y - 12}
                textAnchor="middle"
                className="fill-error"
                style={{ fontSize: 11, fontWeight: 500 }}
              >
                {alertLabel ?? "loss"}
              </text>
            ) : null}

            <text
              x={PADDING.left}
              y={VIEWBOX_HEIGHT - 8}
              className="fill-on-surface-variant"
              style={{ fontSize: 11 }}
            >
              {points[0]!.on}
            </text>
            <text
              x={VIEWBOX_WIDTH - PADDING.right}
              y={VIEWBOX_HEIGHT - 8}
              textAnchor="end"
              className="fill-on-surface-variant"
              style={{ fontSize: 11 }}
            >
              {points[points.length - 1]!.on}
            </text>
          </svg>

          {activePoint ? (
            <p
              aria-live="polite"
              className="text-body text-on-surface-variant"
              data-numeric
            >
              {activePoint.on} · <span className="text-on-surface">{activePoint.grams} g</span>
            </p>
          ) : null}
        </div>
      )}
    </figure>
  );
}

function buildGeometry(points: readonly WeightPoint[]) {
  if (points.length === 0) {
    return { coords: [], gridLines: [] };
  }

  const values = points.map((p) => p.grams);
  const min = Math.min(...values);
  const max = Math.max(...values);

  // Padded so a flat series is not drawn as a line jammed against an edge, and a
  // one-point series still has a sensible scale.
  const span = max - min || Math.max(1, max * 0.1);
  const low = Math.floor(min - span * 0.2);
  const high = Math.ceil(max + span * 0.2);

  const plotWidth = VIEWBOX_WIDTH - PADDING.left - PADDING.right;
  const plotHeight = VIEWBOX_HEIGHT - PADDING.top - PADDING.bottom;

  const coords = points.map((point, index) => ({
    x:
      PADDING.left +
      (points.length === 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth),
    y: PADDING.top + plotHeight - ((point.grams - low) / (high - low)) * plotHeight,
  }));

  const gridLines = [low, Math.round((low + high) / 2), high].map((value) => ({
    value,
    y: PADDING.top + plotHeight - ((value - low) / (high - low)) * plotHeight,
  }));

  return { coords, gridLines };
}
